import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/**
 * useScrollReveal
 * Runs a GSAP callback scoped to the returned ref and cleans up every
 * tween/ScrollTrigger it creates on unmount. Deliberately avoids
 * ScrollTrigger `pin` (which inserts DOM nodes React doesn't know about)
 * and any smooth-scroll library — plain native scroll, toggled reveals
 * and light scrub-based parallax only, so it stays fast and doesn't
 * fight React's own DOM management.
 */
export default function useScrollReveal(callback, deps = []) {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return undefined;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    let ctx;
    if (!reduceMotion) {
      ctx = gsap.context(() => {
        callback({ el: ref.current, gsap, ScrollTrigger });
      }, ref);
    }

    return () => {
      if (ctx) ctx.revert();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return ref;
}

/** Shared defaults for a simple fade-up reveal, used across sections. */
export const revealUp = (target, trigger, extra = {}) => ({
  y: 36,
  opacity: 0,
  duration: 0.7,
  ease: "power3.out",
  scrollTrigger: {
    trigger: trigger || target,
    start: "top 85%",
    toggleActions: "play none none none",
  },
  ...extra,
});
